'use client';

import { useEffect } from 'react';
import { classifyAiReferral } from '@/lib/ai-referral-sources';

const SESSION_KEY = 'mfm-ai-referral-tracked';

/**
 * Records visits that arrive from an AI assistant (ChatGPT, Perplexity,
 * Claude, Gemini, Grok, Copilot, ...). Runs once per browser session on the
 * landing page only; document.referrer is empty on internal navigations, so
 * later route changes never produce false positives. Stores no cookies and no
 * PII, so it does not need cookie consent (unlike GA).
 */
export default function AiReferralTracker() {
  useEffect(() => {
    try {
      if (window.location.pathname.startsWith('/admin')) return;
      if (sessionStorage.getItem(SESSION_KEY)) return;

      const referrer = document.referrer || null;
      // Never treat internal navigation as a referral
      if (referrer) {
        try {
          if (new URL(referrer).hostname === window.location.hostname) return;
        } catch {
          // unparseable referrer, let the classifier decide
        }
      }

      const utmSource = new URLSearchParams(window.location.search).get('utm_source');

      // Client-side pre-check so non-AI visitors never make a network call.
      // The server re-classifies from the raw signals before storing.
      if (!classifyAiReferral(referrer, utmSource)) return;

      sessionStorage.setItem(SESSION_KEY, '1');

      void fetch('/api/track/ai-referral', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          referrer,
          utmSource,
          landingPage: window.location.pathname,
        }),
        keepalive: true,
      }).catch(() => {
        // Tracking is best-effort; never surface errors to visitors
      });
    } catch {
      // sessionStorage can throw in private browsing; tracking is optional
    }
  }, []);

  return null;
}
